// ═══════════════════════════════════════════════════════════════════════════════
// Tappable list row — border card + optional chevron / corner ribbon
// ═══════════════════════════════════════════════════════════════════════════════

import type { KeyboardEvent, ReactNode } from 'react';
import { ListRowChevron } from './ListRowChevron';

export type ListNavRowAlign = 'center' | 'start';
export type ListNavRowTone = 'default' | 'danger';
export type ListNavRowRoot = 'button' | 'div';

type ListNavRowClassNameOptions = {
  align?: ListNavRowAlign;
  tone?: ListNavRowTone;
  /** Roomier padding when the row has a leading time block. */
  comfortable?: boolean;
  /** Layout / spacing only — do not override colors. */
  className?: string;
};

type ListNavRowProps = ListNavRowClassNameOptions & {
  children: ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
  showChevron?: boolean;
  /** Absolutely positioned status ribbon (e.g. CornerStatusRibbon). */
  ribbon?: ReactNode;
  /** Left-side slot (due-time block or status icon). */
  leading?: ReactNode;
  /** Right-side slot before the chevron (e.g. ⋮ menu — use root="div"). */
  trailing?: ReactNode;
  disabled?: boolean;
  /**
   * `div` when the row contains another button (overflow menu).
   * Defaults to `button` when `onClick` is set, otherwise `div`.
   */
  root?: ListNavRowRoot;
};

const SURFACE_CLASS =
  'relative isolate flex w-full gap-3 overflow-hidden rounded-xl border bg-white text-left shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-gray-900 dark:shadow-black/20';

const TONE_CLASS: Record<ListNavRowTone, string> = {
  default:
    'border-gray-200/80 hover:border-primary-200 dark:border-gray-700 dark:hover:border-primary-600',
  danger:
    'border-red-100 hover:border-red-200 dark:border-red-900/40 dark:hover:border-red-800',
};

const ALIGN_CLASS: Record<ListNavRowAlign, string> = {
  center: 'items-center',
  start: 'items-start',
};

const PADDING_DEFAULT_CLASS = 'py-3.5 pl-4 pr-1';
const PADDING_COMFORTABLE_CLASS = 'p-4 pr-1';

/** Shared class for `<Link>` / custom roots that cannot use `<ListNavRow>`. */
export const listNavRowClassName = ({
  align = 'center',
  tone = 'default',
  comfortable = false,
  className = '',
}: ListNavRowClassNameOptions = {}): string =>
  [
    SURFACE_CLASS,
    TONE_CLASS[tone],
    ALIGN_CLASS[align],
    comfortable ? PADDING_COMFORTABLE_CLASS : PADDING_DEFAULT_CLASS,
    className,
  ]
    .filter(Boolean)
    .join(' ');

/** Default list-row class (center + default tone). Prefer `listNavRowClassName` for variants. */
export const LIST_NAV_ROW_CLASS = listNavRowClassName();

export const ListNavRow = ({
  children,
  onClick,
  ariaLabel,
  className = '',
  showChevron = true,
  ribbon,
  leading,
  trailing,
  disabled = false,
  align = 'center',
  tone = 'default',
  comfortable = false,
  root,
}: ListNavRowProps) => {
  const resolvedRoot: ListNavRowRoot = root ?? (onClick ? 'button' : 'div');
  const rootClassName = listNavRowClassName({ align, tone, comfortable, className });

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onClick?.();
  };

  const inner = (
    <>
      {ribbon}
      {leading ? <div className="relative z-[1] shrink-0">{leading}</div> : null}
      <div className="relative z-[1] min-w-0 flex-1">{children}</div>
      {trailing ? <div className="relative z-[1] shrink-0 self-center">{trailing}</div> : null}
      {showChevron ? <ListRowChevron /> : null}
    </>
  );

  if (resolvedRoot === 'button') {
    return (
      <button
        type="button"
        onClick={onClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={ariaLabel}
        className={`${rootClassName} disabled:cursor-not-allowed disabled:opacity-50`}
      >
        {inner}
      </button>
    );
  }

  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : onClick}
        onKeyDown={disabled ? undefined : handleKeyDown}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        className={`${rootClassName} ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      >
        {inner}
      </div>
    );
  }

  return (
    <div className={rootClassName} aria-label={ariaLabel}>
      {inner}
    </div>
  );
};
